import { Button, Card, CardContent, LoadingState } from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';
import { Hammer } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { listWorkOrders } from '../../../api/work-orders.js';
import { CreateWorkOrderSheet } from '../work-orders/create-sheet.js';

import type { UnitRow } from '../../../api/buildings.js';
import type { UnitDetailBuilding } from '../../../api/units.js';

interface Props {
  unit: UnitRow;
  building: UnitDetailBuilding;
}

/**
 * Work orders scoped to this unit. Mirrors the Building > Work orders
 * tab in shape, but pre-filters with `unitId` and pre-fills the new
 * work-order sheet so creating a WO from this surface always lands
 * on the right unit.
 */
export function WorkOrdersTab({ unit, building }: Props) {
  const navigate = useNavigate();
  const [openCreate, setOpenCreate] = useState(false);

  const woQuery = useQuery({
    queryKey: ['work-orders', { unitId: unit.id }],
    queryFn: () => listWorkOrders({ unitId: unit.id, limit: 25 }),
  });
  const rows = woQuery.data?.workOrders ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Link
          to={`/work-orders?unitId=${unit.id}`}
          className="text-sm text-primary hover:underline"
        >
          View all work orders for this unit →
        </Link>
        <Button size="sm" onClick={() => setOpenCreate(true)}>
          <Hammer className="h-3.5 w-3.5" /> New work order
        </Button>
      </div>
      {woQuery.isLoading ? (
        <LoadingState rows={3} />
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm text-fg-secondary">
            No work orders for this unit yet.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="divide-y divide-border p-0">
            {rows.map((wo) => (
              <button
                key={wo.id}
                onClick={() => navigate(`/work-orders/${wo.id}`)}
                className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left text-sm hover:bg-surface-muted"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span className="font-mono text-xs text-fg-muted">{wo.displayNumber}</span>
                  <span className="truncate font-medium">{wo.title}</span>
                </div>
                <span className="text-xs capitalize text-fg-muted">{wo.severity}</span>
              </button>
            ))}
          </CardContent>
        </Card>
      )}
      <CreateWorkOrderSheet
        open={openCreate}
        onOpenChange={setOpenCreate}
        buildingId={building.id}
        unitId={unit.id}
        onCreated={(wo) => {
          setOpenCreate(false);
          navigate(`/work-orders/${wo.id}`);
        }}
      />
    </div>
  );
}
